// Pricing plans configuration
// Maps to Stripe price IDs from backend/api/api/config/stripe_config.py

export const PRICING_PLANS = {
  free: {
    name: "Free",
    description: "Perfect for trying out our platform",
    price: 0,
    minutes: 60,
    hours: 1,
    // No AI Agent video on the free plan since 2026-10-27 (a pack or Pro); the API's DEFAULT_PLAN_DETAILS
    aiAgentVideos: 0,
    repromptingAttempts: 1,
    // Per AI video. A backend test keeps these equal to DEFAULT_PLAN_DETAILS
    aiOps: { imageRedraws: 3, imageEdits: 0, animations: 0, voiceRedos: 0 },
    features: [
      {
        text: "1 hour of video processing per month",
        highlight: true,
        subItems: [
          {
            text: "AI clipping of long videos to shorts",
            subItems: ["1 reprompting attempt per video"]
          },
          "Auto captioning videos",
          "Reframing videos to 9:16, 1:1, 4:5 or 16:9",
          "Rendering editor projects"
        ]
      },
      { text: "Access to video editor", highlight: false }
    ],
    cta: "Get Started Free",
    popular: false
  },
  hobbyist: {
    name: "Hobbyist",
    description: "For casual creators and hobbyists",
    minutes: 300,
    hours: 5,
    aiAgentVideos: 15,
    repromptingAttempts: 1,
    monthly: {
      price: 18,
      originalPrice: 25,  // Shows 28% OFF
      priceId: import.meta.env.MODE === 'production'
        ? "price_1SXFxgLjtTPk6EaHqeQceOkF"
        : "price_1SXG0PLjtTPk6EaHBuBAgZ0A"
    },
    annual: {
      price: 180,
      originalPrice: 216,  // 18 * 12 = 216
      pricePerMonth: 15,   // 180 / 12 = 15
      originalPricePerMonth: 18,
      priceId: import.meta.env.MODE === 'production'
        ? "price_1SXFxgLjtTPk6EaHNTNbPhuH"
        : "price_1SXG0PLjtTPk6EaHbAPVqkPV"
    },
    features: [
      {
        text: "5 hours of video processing per month",
        highlight: true,
        subItems: [
          {
            text: "AI clipping of long videos to shorts",
            subItems: ["1 reprompting attempt per video"]
          },
          "Auto captioning videos",
          "Reframing videos to 9:16, 1:1, 4:5 or 16:9",
          "Rendering editor projects"
        ]
      },
      {
        text: "15 AI Agent videos per month",
        highlight: true,
        subItems: [
          "15 image regenerations per project",
          "10 image edits per project",
          "5 image-to-video animations per project",
          "15 voice regenerations per project"
        ]
      },
      { text: "No watermarks on exports", highlight: true },
      { text: "Access to video editor", highlight: false },
      { text: "Standard email support", highlight: false }
    ],
    cta: "Start Creating",
    popular: false
  },
  starter: {
    name: "Starter",
    description: "For regular content creators",
    minutes: 600,
    hours: 10,
    aiAgentVideos: 30,
    repromptingAttempts: 3,
    monthly: {
      price: 35,  // Increased from 18
      originalPrice: 45,  // Adjusted for discount display
      priceId: import.meta.env.MODE === 'production'
        ? "price_1SWqSILjtTPk6EaHbDkK2FSH"  // Production - Starter - Monthly
        : "price_1RHlPQLjtTPk6EaH81ZishHX"  // Development - Starter - Monthly
    },
    annual: {
      price: 350,  // Increased from 180
      originalPrice: 420, // 35 * 12 = 420
      pricePerMonth: 29.17, // 350 / 12 = 29.17
      originalPricePerMonth: 35,
      priceId: import.meta.env.MODE === 'production'
        ? "price_1SWqW6LjtTPk6EaH5gqPcacE"  // Production - Starter - Annual
        : "price_1RHlRfLjtTPk6EaHTmfpptK5"  // Development - Starter - Annual
    },
    features: [
      {
        text: "10 hours of video processing per month",
        highlight: true,
        subItems: [
          {
            text: "AI clipping of long videos to shorts",
            subItems: ["3 reprompting attempts per video"]
          },
          "Auto captioning videos",
          "Reframing videos to 9:16, 1:1, 4:5 or 16:9",
          "Rendering editor projects"
        ]
      },
      {
        text: "30 AI Agent videos per month",
        highlight: true,
        subItems: [
          "15 image regenerations per project",
          "10 image edits per project",
          "5 image-to-video animations per project",
          "15 voice regenerations per project"
        ]
      },
      { text: "No watermarks on exports", highlight: true },
      { text: "Access to video editor", highlight: false },
      { text: "Priority email support", highlight: false }
    ],
    cta: "Start Creating",
    popular: true
  },
  pro: {
    name: "Pro",
    description: "For professional creators and agencies",
    minutes: 1800,
    hours: 30,
    aiAgentVideos: 90,
    repromptingAttempts: "Unlimited",
    monthly: {
      price: 99,  // Increased from 48
      originalPrice: 120,  // Adjusted for discount display
      priceId: import.meta.env.MODE === 'production'
        ? "price_1SWqXzLjtTPk6EaHgXrxQSeC"  // Production - Pro - Monthly
        : "price_1RHmKtLjtTPk6EaHRxdS7QOZ"  // Development - Pro - Monthly
    },
    annual: {
      price: 990,  // Increased from 420
      originalPrice: 1188, // 99 * 12 = 1188
      pricePerMonth: 82.50, // 990 / 12 = 82.50
      originalPricePerMonth: 99,
      priceId: import.meta.env.MODE === 'production'
        ? "price_1SWqZPLjtTPk6EaHngsJcaHE"  // Production - Pro - Annual
        : "price_1RJVOpLjtTPk6EaHYIVWzzC4"  // Development - Pro - Annual
    },
    features: [
      {
        text: "30 hours of video processing per month",
        highlight: true,
        subItems: [
          {
            text: "AI clipping of long videos to shorts",
            subItems: ["Unlimited reprompting attempts"]
          },
          "Auto captioning videos",
          "Reframing videos to 9:16, 1:1, 4:5 or 16:9",
          "Rendering editor projects"
        ]
      },
      {
        text: "90 AI Agent videos per month",
        highlight: true,
        subItems: [
          "Unlimited image regenerations per project",
          "Unlimited image edits per project",
          "Unlimited image-to-video animations per project",
          "Unlimited voice regenerations per project"
        ]
      },
      { text: "No watermarks on exports", highlight: true },
      { text: "Access to video editor", highlight: false },
      { text: "Priority support", highlight: true }
    ],
    cta: "Go Pro",
    popular: false
  },
  proPackage: {
    name: "Pro",
    description: "Flexible pricing - choose the bundle size that fits your needs",
    isQuantityBased: true,  // Flag for special handling
    perPackage: {
      minutes: 300,  // 5 hours
      hours: 5,
      aiAgentVideos: 10,
      repromptingAttempts: 3,
      // Per AI video, per bundle: the API multiplies every per-package limit by the
      // bundle count, these included. A backend test keeps them equal to the Pro
      // Package's per_package_limits
      aiOps: { imageRedraws: 15, imageEdits: 10, animations: 5, voiceRedos: 15 },
    },
    monthly: {
      pricePerPackage: 19,  // US$19 since 2026-10-25 (was 15); the API's services/local_pricing.py PRICES
      priceId: import.meta.env.MODE === 'production'
        ? "price_1UUFCeLjtTPk6EaHYuJsA2hE"  // Production - Pro - Monthly (US$19/mo, with the local currencies)
        : "price_1TCBLmLjtTPk6EaHyQMuV4Tz"  // Test - Pro - Monthly ($15/mo)
    },
    annual: {
      pricePerPackage: 190,  // US$190/year per bundle since 2026-10-26 (10 months of US$19: 2 months free; it was US$100)
      pricePerMonth: 15.83,  // $190/12 ≈ $15.83/mo per bundle
      priceId: import.meta.env.MODE === 'production'
        ? "price_1UUnuQLjtTPk6EaHmQfbUfY1"  // Production - Pro - Annual (US$190/yr, with the local currencies; US$100 was price_1UHl9OLjtTPk6EaHInLvzD28)
        : "price_1Sd55PLjtTPk6EaHUNODhuJF"  // Test - Pro - Annual
    },
    minQuantity: 1,
    maxQuantity: 10,
    defaultQuantity: 1,  // Default to 1x (US$19) entry level
    features: [
      {
        text: "Flexible bundle-based pricing",
        highlight: true,
        subItems: [
          "Each bundle = 5 hours video processing + 10 AI videos",
          "Choose 1-10 bundles to match your needs",
          "Scale up or down anytime"
        ]
      },
      {
        text: "3 reprompting attempts per video",
        highlight: false
      },
      {
        text: "No watermarks on exports",
        highlight: true
      },
      {
        text: "Access to video editor",
        highlight: false
      },
      {
        text: "Priority email support",
        highlight: false
      }
    ],
    cta: "Get Started with Pro",
    popular: true
  }
};
